/**
 * HDN · Barra fija de compra de la ficha (paquete F), portada del tema anterior (Ella: sticky-add-to-cart)
 * sobre el formulario de Horizon y sin jQuery. Marcado en sections/hdn-barra-compra.liquid.
 *
 * - Aparece cuando el bloque de compra de la ficha queda por encima de la pantalla y se va al volver
 *   (o si aún no se ha llegado a él). Entra y sale con translate y opacidad; sin movimiento con
 *   «reducir movimiento» (lo pone el CSS).
 * - Variante y cantidad sincronizadas con las de la ficha en los dos sentidos: al elegir en la barra
 *   se pulsan los valores en el selector de la ficha (Horizon pide la variante y actualiza su
 *   formulario); al cambiar en la ficha, la barra se pone al día.
 * - «Agregar al carrito» pulsa el botón de la ficha, como la barra de Horizon: el carrito, los avisos
 *   y el cajón son los de Horizon.
 * - Mientras se ve, los botones flotantes de las esquinas de abajo (WhatsApp, accesibilidad) suben
 *   para no tapar la barra.
 */
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

class HdnBarraCompra extends HTMLElement {
  connectedCallback() {
    this.seccion = document.querySelector('.product-information, product-information')?.closest('.shopify-section') ?? document;
    this.formulario = this.seccion.querySelector('product-form-component') ?? document.querySelector('product-form-component');
    this.bloqueCompra = this.formulario?.closest('.buy-buttons-block') ?? this.formulario;
    this.selector = this.querySelector('[data-variante]');
    this.cantidad = this.querySelector('[data-cantidad-campo]');
    this.agregar = this.querySelector('[data-agregar]');
    this.opciones = JSON.parse(this.querySelector('[data-variantes]')?.textContent || '[]');
    this.levantados = new Map();
    if (!this.formulario || !this.bloqueCompra) return;

    this.alPulsar = (event) => this.pulsar(event);
    this.alElegirVariante = () => this.elegirVariante();
    this.alEscribirCantidad = () => this.ponerCantidadEnFicha();
    this.alCambiarVarianteFicha = (event) => this.varianteDeLaFicha(event);
    this.alCambiarCantidadFicha = (event) => this.cantidadDeLaFicha(event);

    this.addEventListener('click', this.alPulsar);
    this.selector?.addEventListener('change', this.alElegirVariante);
    this.cantidad?.addEventListener('change', this.alEscribirCantidad);
    document.addEventListener(StandardEvents.productSelect, this.alCambiarVarianteFicha);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.alCambiarCantidadFicha);

    // Visible solo cuando el bloque de compra de la ficha ha quedado por encima de la pantalla.
    this.vigilante = new IntersectionObserver(([entrada]) => {
      if (!entrada) return;
      const pasado = !entrada.isIntersecting && entrada.boundingClientRect.bottom < 0;
      this.mostrar(pasado);
    });
    this.vigilante.observe(this.bloqueCompra);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.alPulsar);
    this.selector?.removeEventListener('change', this.alElegirVariante);
    this.cantidad?.removeEventListener('change', this.alEscribirCantidad);
    document.removeEventListener(StandardEvents.productSelect, this.alCambiarVarianteFicha);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.alCambiarCantidadFicha);
    this.vigilante?.disconnect();
    this.bajarFlotantes();
  }

  mostrar(visible) {
    if (visible === this.hasAttribute('data-visible')) return;
    this.toggleAttribute('data-visible', visible);
    // Fuera de la vista no se puede llegar a ella con el tabulador ni la lee el lector de pantalla.
    this.inert = !visible;
    if (visible) this.subirFlotantes();
    else this.bajarFlotantes();
  }

  /** Los botones flotantes fijos de las esquinas de abajo suben lo que mide la barra. */
  subirFlotantes() {
    const alto = this.getBoundingClientRect().height;
    const y = window.innerHeight - 20;
    const puntos = [16, 40, window.innerWidth - 40, window.innerWidth - 16];
    for (const x of puntos) {
      for (const elemento of document.elementsFromPoint(x, y)) {
        if (this.contains(elemento) || elemento === document.body || elemento === document.documentElement) continue;
        const fijo = this.fijoMasCercano(elemento);
        if (!fijo || this.levantados.has(fijo) || this.contains(fijo)) continue;
        this.levantados.set(fijo, fijo.style.translate);
        fijo.style.transition = 'translate 250ms cubic-bezier(0.23, 1, 0.32, 1)';
        fijo.style.translate = `0 -${Math.round(alto)}px`;
      }
    }
  }

  bajarFlotantes() {
    for (const [fijo, antes] of this.levantados) fijo.style.translate = antes;
    this.levantados.clear();
  }

  fijoMasCercano(elemento) {
    for (let actual = elemento; actual && actual !== document.body; actual = actual.parentElement) {
      if (getComputedStyle(actual).position === 'fixed') return actual;
    }
    return null;
  }

  pulsar(event) {
    const paso = event.target.closest('[data-cantidad]');
    if (paso && this.cantidad) {
      this.cantidad.value = String(Math.max(1, (parseInt(this.cantidad.value, 10) || 1) + Number(paso.dataset.cantidad)));
      this.ponerCantidadEnFicha();
      return;
    }
    if (event.target.closest('[data-agregar]')) {
      this.ponerCantidadEnFicha();
      this.formulario.querySelector('[ref="addToCartButton"]')?.click();
    }
  }

  /** La variante elegida en la barra, en el selector de la ficha (se pulsan sus valores). */
  elegirVariante() {
    const indice = this.selector.selectedIndex;
    const valores = this.opciones[indice];
    const selector = this.seccion.querySelector('variant-picker');
    if (!valores || !selector) return;
    const grupos = selector.querySelectorAll('fieldset');
    valores.forEach((valor, posicion) => {
      const grupo = grupos[posicion];
      const campo = [...(grupo?.querySelectorAll('input[type="radio"]') ?? [])].find((input) => input.value === valor);
      if (campo && !campo.checked) campo.click();
      const lista = grupo?.querySelector('select') ?? (grupos.length ? null : selector.querySelectorAll('select')[posicion]);
      if (lista && lista.value !== valor) {
        lista.value = valor;
        lista.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /** La cantidad de la barra, en la de la ficha. */
  ponerCantidadEnFicha() {
    if (!this.cantidad) return;
    const valor = String(Math.max(1, parseInt(this.cantidad.value, 10) || 1));
    this.cantidad.value = valor;
    const campo = this.formulario.querySelector('input[name="quantity"]');
    if (!campo || campo.value === valor) return;
    const componente = campo.closest('quantity-selector-component');
    if (componente?.setValue) componente.setValue(valor);
    else campo.value = valor;
  }

  /** Al cambiar la variante en la ficha, la barra se pone al día. */
  async varianteDeLaFicha(event) {
    if (!this.selector || event.target?.closest?.('product-card, quick-add-dialog, hdn-barra-compra')) return;
    try {
      const { detail } = await event.promise;
      const id = detail?.resource?.id;
      if (id != null && this.selector.querySelector(`option[value="${id}"]`)) this.selector.value = String(id);
    } catch {
      // Petición cancelada por otra más nueva: ya llegará esa.
    }
  }

  /** Al cambiar la cantidad en la ficha (no en el carrito), la barra se pone al día. */
  cantidadDeLaFicha(event) {
    if (!this.cantidad || !this.formulario.contains(event.target)) return;
    const valor = event.detail?.quantity;
    if (Number.isFinite(valor) && valor > 0) this.cantidad.value = String(valor);
  }
}

if (!customElements.get('hdn-barra-compra')) customElements.define('hdn-barra-compra', HdnBarraCompra);
